import { apiDelete, apiGet, apiPatch, apiPost } from '../../api/oauth/helpers.ts';

const BASE = '/api/sop-synthesizer';

export type SopStatus =
  | 'generating'
  | 'draft'
  | 'in_review'
  | 'changes_requested'
  | 'approved'
  | 'published'
  | 'archived';
export type SopRunStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
export type SopSectionKind =
  | 'purpose'
  | 'roles'
  | 'systems'
  | 'schedule'
  | 'procedure'
  | 'evidence'
  | 'exceptions'
  | 'open_items'
  | 'caveats';

/** How far along a video's media preparation is, as the tenant sees it. Only "ready" videos can start a SOP. */
export type SopMediaStatus = 'ready' | 'pending' | 'processing' | 'failed' | 'expired';

export interface SopVideoOption {
  id: string;
  title: string;
  original_filename: string;
  duration_seconds?: number;
  size_bytes: number;
  media_status: SopMediaStatus;
  uploaded_at?: string;
  processed_at?: string;
}

export interface SopRun {
  id: string;
  sop_id: string;
  status: SopRunStatus;
  stage?: string;
  progress: number;
  message?: string;
  error_message?: string;
  model?: string;
  input_tokens: number;
  output_tokens: number;
  instructions?: string;
  created_at?: string;
  started_at?: string;
  finished_at?: string;
}

export interface Sop {
  id: string;
  organization_id: string;
  video_id: string;
  title: string;
  description: string;
  status: SopStatus;
  row_version: number;
  created_at?: string;
  updated_at?: string;
}

export interface SopSummary extends Sop {
  video_title?: string;
  latest_run?: SopRun;
}

export interface SopSection {
  id: string;
  sop_id: string;
  position: number;
  kind: SopSectionKind;
  heading: string;
  body: string;
  locked: boolean;
  row_version: number;
}

export interface SopCitation {
  id: string;
  section_id: string;
  marker: string;
  start_seconds: number;
  end_seconds: number;
  quote: string;
  frame_artifact_id?: string;
  frame_url?: string;
}

export interface SopDetail {
  sop: Sop;
  sections: SopSection[];
  citations: SopCitation[];
  /** artifact id -> signed URL, for screenshots referenced as frame:<id> in section bodies */
  frames: Record<string, string>;
  run?: SopRun;
  video: { id: string; title?: string; duration_seconds?: number; url?: string };
}

export async function fetchVideos(): Promise<SopVideoOption[]> {
  const data = await apiGet(`${BASE}/videos`);
  return Array.isArray(data?.videos) ? data.videos : [];
}

export async function fetchSops(): Promise<SopSummary[]> {
  const data = await apiGet(`${BASE}/sops`);
  return Array.isArray(data?.sops) ? data.sops : [];
}

export async function fetchSop(id: string): Promise<SopDetail> {
  return apiGet(`${BASE}/sops/${id}`);
}

export interface CreateSopBody {
  video_id: string;
  title: string;
  description?: string;
  instructions?: string;
  /** Start writing the draft right away (default true). False saves an empty draft. */
  generate?: boolean;
}

export async function createSop(body: CreateSopBody): Promise<{ sop: Sop; run: SopRun | null }> {
  return apiPost(`${BASE}/sops`, body);
}

export async function updateSop(id: string, body: { title?: string; description?: string }): Promise<Sop> {
  return (await apiPatch(`${BASE}/sops/${id}`, body)).sop;
}

export async function regenerateSop(id: string, instructions?: string): Promise<{ sop: Sop; run: SopRun }> {
  return apiPost(`${BASE}/sops/${id}/generate`, { instructions: instructions ?? '' });
}

export async function cancelSopGeneration(id: string): Promise<void> {
  await apiPost(`${BASE}/sops/${id}/cancel`, {});
}

export async function deleteSop(id: string): Promise<void> {
  await apiDelete(`${BASE}/sops/${id}`);
}

/** A SOP can be edited or deleted until it enters review; after that changes go through the review flow. */
export function isSopEditable(status: SopStatus): boolean {
  return status === 'generating' || status === 'draft' || status === 'changes_requested';
}

export const LOCKED_SOP_MESSAGE = "SOPs that are in review, approved, or published can't be deleted.";
